// @ts-nocheck
// DID view details: the shared per-call report (call-report/CallReportView) locked
// to one DID, opened from the Incoming / Outgoing Summary. The helpers exported
// above the page (IVR journey, webhooks, report legs, export media) are shared
// with CdrCallDetailDialog, CampaignReportPage and the call report.
import CallReportView from './call-report/CallReportView';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';

import { ROUTES } from '@/utilities/constant';
import moment from 'moment';
import { API_INCOMING_REPORT_BY_DID_LIST, API_OUTGOING_REPORT_BY_DID_LIST } from '@/service/url';
import { Play, Mic, Grid3x3, PhoneOff, FileText, Clock, Phone, CheckCircle2, X, Bot } from 'lucide-react';
import { isAiIvrEventType, queueToCallGroup, summarizeEventData } from './components/callFlowModel';

import { maskIfEnabled, maskNumberForHangup } from '@/utilities/cliDisplay';
import { fetchCdrMediaDetails, getRecordingData, hasCdrMediaReference, parseCdrMediaReference } from '@/utilities/cdrMedia';
import MediaAudioPlayer from '@/components/partials/components/MediaAudioPlayer';
import { CallEngineTracesDialog } from './components/CallEngineTraces';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft } from 'lucide-react';

export const parseIvrJourney = (ivrJourneyRaw: any) => {
  try {
    if (ivrJourneyRaw == null || ivrJourneyRaw === '') return [];

    const parsed =
      typeof ivrJourneyRaw === 'string'
        ? JSON.parse(ivrJourneyRaw)
        : ivrJourneyRaw;

    if (!Array.isArray(parsed)) return [];
    return parsed.filter(Boolean);
  } catch (err) {
    console.log('IVR journey parse error:', err);
    return [];
  }
};

export const parseCallWebhooks = (webhooksRaw: any) => {
  try {
    if (webhooksRaw == null || webhooksRaw === '') return [];

    const parsed =
      typeof webhooksRaw === 'string' ? JSON.parse(webhooksRaw) : webhooksRaw;

    if (!Array.isArray(parsed)) return [];
    return parsed.filter(Boolean);
  } catch (err) {
    console.log('Webhooks parse error:', err);
    return [];
  }
};

export const getWebhooksForCalls = (mainCall: any, relatedCalls: any = []) => {
  const candidates = [mainCall, ...relatedCalls].filter(Boolean);
  for (const call of candidates) {
    const hooks = parseCallWebhooks(call?.webhooks);
    if (hooks.length > 0) return hooks;
  }
  return [];
};

// Regular webhook events carry `success: true`; API node events carry
// `status: "success"` and no `success` field.
const isWebhookSuccess = (hook: any) =>
  hook?.success === true ||
  hook?.success === 'true' ||
  String(hook?.status || '').toLowerCase() === 'success';

const formatWebhookType = (hook: any) => {
  const type = String(hook?.type || 'WEBHOOK').replace(/_/g, ' ');
  const eventType = hook?.eventType ? String(hook.eventType).replace(/_/g, ' ') : '';
  return eventType ? `${type} • ${eventType}` : type;
};

const parseIvrEventData = (data: any) => {
  if (data == null || data === '') return {};
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

export const getIvrJourneyForCalls = (mainCall: any, relatedCalls: any = []) => {
  const candidates = [mainCall, ...relatedCalls].filter(Boolean);
  for (const call of candidates) {
    const journey = parseIvrJourney(call?.ivr_journey);
    if (journey.length > 0) return journey;
  }
  return [];
};

const formatIvrJourneyTime = (dateString: any) => {
  if (!dateString) return '-';
  try {
    // IVR engine stamps are UTC. A value with no zone is parsed as the
    // browser's local time, so an IST machine shows the UTC clock unchanged.
    const raw = String(dateString).trim();
    const hasZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw);
    const normalized = hasZone
      ? raw
      : (raw.includes('T') ? raw : raw.replace(' ', 'T')) + 'Z';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '-';
  }
};

const getIvrEventVisual = (type: string, data: any = {}) => {
  const status = String(data?.status || '').toUpperCase();

  if (isAiIvrEventType(type)) {
    return {
      tone: 'neutral',
      icon: 'bot',
      cardBg: '#f5f3ff',
      cardBorder: '#c4b5fd',
      iconBg: '#7c3aed',
    };
  }
  if (type === 'IVR_START') {
    return {
      tone: 'success',
      icon: 'play',
      cardBg: '#f0fdf4',
      cardBorder: '#86efac',
      iconBg: '#16a34a',
    };
  }
  if (type === 'CALL_ENDED' || type === 'HANGUP') {
    return {
      tone: 'success',
      icon: 'phone-off',
      cardBg: '#f0fdf4',
      cardBorder: '#86efac',
      iconBg: '#16a34a',
    };
  }
  if (type === 'DTMF') {
    const isPrompting = status === 'PROMPTING' || data.digit == null || data.digit === '';
    return {
      tone: 'input',
      icon: isPrompting ? 'mic' : 'keypad',
      cardBg: '#fff1f2',
      cardBorder: '#fecaca',
      iconBg: '#ef4444',
    };
  }
  if (type === 'PLAY') {
    return {
      tone: 'success',
      icon: 'play',
      cardBg: '#f0fdf4',
      cardBorder: '#86efac',
      iconBg: '#16a34a',
    };
  }
  if (type === 'TRANSFER') {
    return {
      tone: 'neutral',
      icon: 'phone',
      cardBg: '#eff6ff',
      cardBorder: '#93c5fd',
      iconBg: '#2563eb',
    };
  }
  return {
    tone: 'neutral',
    icon: 'phone',
    cardBg: '#f8fafc',
    cardBorder: '#e2e8f0',
    iconBg: '#64748b',
  };
};

// agentMask: viewer is an agent with number masking on.
export const formatIvrJourneyStep = (event: any, agentMask = false) => {
  const type = String(event?.type || event?.eventname || 'Unknown').toUpperCase();
  const data = parseIvrEventData(event?.data);
  const visual = getIvrEventVisual(type, data);
  const status = String(data?.status || '').toUpperCase();
  let title = queueToCallGroup(type.replace(/_/g, ' '));
  const detailItems: any[] = [];
  let ok = true;

  switch (type) {
    case 'IVR_START':
      title = 'IVR Started';
      if (data.flowName) detailItems.push({ label: 'Flow', value: data.flowName });
      if (data.context) detailItems.push({ label: 'Context', value: data.context });
      if (data.caller) detailItems.push({ label: 'Caller', value: data.caller });
      if (data.did) detailItems.push({ label: 'DID', value: data.did });
      break;
    case 'PLAY':
      title = data.status === 'STARTED' ? 'Playing Audio' : `Play (${data.status || '-'})`;
      if (data.nodeId != null) detailItems.push({ label: 'Node', value: data.nodeId });
      if (data.level != null) detailItems.push({ label: 'Level', value: data.level });
      if (data.audio) detailItems.push({ label: 'Audio', value: data.audio, isLink: true });
      break;
    case 'DTMF':
      if (status === 'PROMPTING' || data.digit == null || data.digit === '') {
        title = 'DTMF Input - Prompting';
        if (data.status) detailItems.push({ label: 'Status', value: data.status });
        if (data.numDigits != null) detailItems.push({ label: 'Digits expected', value: data.numDigits });
        if (data.menuAudio) detailItems.push({ label: 'Menu', value: data.menuAudio, isLink: true });
        else if (data.audio) detailItems.push({ label: 'Audio', value: data.audio, isLink: true });
      } else {
        title = 'DTMF Input';
        detailItems.push({ label: 'Digits', value: data.digit });
        if (data.status) detailItems.push({ label: 'Status', value: data.status });
      }
      ok = !data.status || status === 'VALID';
      break;
    case 'TRANSFER':
      title = data.transferType
        ? `Transfer: ${queueToCallGroup(String(data.transferType).replace(/_/g, ' '))}`
        : 'Transfer';
      if (data.transferName) detailItems.push({ label: 'Target', value: data.transferName });
      if (data.status) detailItems.push({ label: 'Status', value: data.status });
      if (data.slotStartTime && data.slotEndTime) {
        detailItems.push({
          label: 'Slot',
          value: `${data.slotStartTime} - ${data.slotEndTime}`,
        });
      }
      ok = !data.status || !['FAILED', 'NO_SLOT', 'REJECTED'].includes(status);
      break;
    case 'HANGUP':
      title = 'Hangup';
      detailItems.push(...summarizeEventData(data));
      break;
    case 'CALL_ENDED':
      title = 'Call Ended';
      if (data.status) detailItems.push({ label: 'Status', value: data.status });
      if (data.context) detailItems.push({ label: 'Context', value: data.context });
      break;
    default:
      // AI / unknown nodes carry whole JSON payloads — show only the outcome
      // fields (reason, intent, result, ...), never the raw object.
      if (isAiIvrEventType(type)) title = 'AI Agent';
      detailItems.push(...summarizeEventData(data));
      ok = !['FAILED', 'ERROR'].includes(status);
  }

  if (agentMask) {
    // Caller / DID / transfer target and any phone-like value in summarised data.
    detailItems.forEach((item) => {
      if (!item.isLink && /^\+?\d{7,15}$/.test(String(item.value ?? '').replace(/[\s-]/g, ''))) {
        item.value = maskIfEnabled(true, item.value);
      }
    });
  }

  return {
    key: `${type}-${event?.time || event?.createdAt || Math.random()}`,
    type,
    title,
    detailItems,
    rawData: data,
    ok,
    visual,
    time: event?.time || event?.createdAt,
  };
};

const renderIvrEventIcon = (iconName: string, size = 16) => {
  const props: any = { size, color: '#ffffff', strokeWidth: 2.5 };
  switch (iconName) {
    case 'play':
      return <Play {...props} />;
    case 'mic':
      return <Mic {...props} />;
    case 'keypad':
      return <Grid3x3 {...props} />;
    case 'phone-off':
      return <PhoneOff {...props} />;
    case 'bot':
      return <Bot {...props} />;
    default:
      return <Phone {...props} />;
  }
};

const renderIvrDetailItems = (items: any[] = []) => {
  if (!items.length) return null;
  return (
    <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6 }}>
      {items.map((item, idx) => (
        <span key={`${item.label}-${idx}`}>
          {idx > 0 ? ' • ' : ''}
          <span style={{ fontWeight: 600, color: '#334155' }}>{item.label}:</span>{' '}
          {item.isLink ? (
            <MediaAudioPlayer
              src={item.value}
              style={{ height: '30px', width: '300px', maxWidth: '100%', minWidth: 'min(300px, 100%)', verticalAlign: 'middle', display: 'inline-block' }}
            />
          ) : (
            <span>{item.value}</span>
          )}
        </span>
      ))}
    </div>
  );
};

export const IvrJourneyPanel = ({ mainCall, steps, agentMask = false }: any) => (
  <div>
    <div
      style={{
        padding: '14px 16px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        background: '#f8fafc',
        marginBottom: '18px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '999px',
          background: '#2563eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Phone size={18} color="#ffffff" strokeWidth={2.5} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>
          {maskNumberForHangup(
            mainCall.caller_number || '-',
            mainCall.hangup_cause,
            agentMask
          )}{' '}
          →{' '}
          {maskNumberForHangup(
            mainCall.callee_number || '-',
            mainCall.hangup_cause,
            agentMask
          )}
        </div>
        <div
          style={{
            fontSize: '12px',
            marginTop: '4px',
            color: '#64748b',
            wordBreak: 'break-word',
          }}
        >
          Call SID: {mainCall.call_sid || '-'} • Trace ID: {mainCall.trace_id || '-'}
          {mainCall.ivr_name ? ` • IVR: ${mainCall.ivr_name}` : ''}
        </div>
      </div>
    </div>

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FileText size={16} color="#64748b" />
        <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
          IVR Event Timeline
        </span>
      </div>
      <span
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#2563eb',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '999px',
          padding: '4px 10px',
        }}
      >
        {steps.length} Events
      </span>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {steps.map((step: any, index: number) => (
        <div key={`${step.key}-${index}`} style={{ display: 'flex', gap: '14px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '34px',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '999px',
                background: step.visual.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(15,23,42,0.12)',
              }}
            >
              {renderIvrEventIcon(step.visual.icon)}
            </div>
            {index < steps.length - 1 ? (
              <div
                style={{
                  width: '2px',
                  flex: 1,
                  minHeight: '18px',
                  background: '#cbd5e1',
                  marginTop: '6px',
                }}
              />
            ) : null}
          </div>

          <div
            style={{
              flex: 1,
              marginBottom: index < steps.length - 1 ? '12px' : 0,
              padding: '12px 14px',
              borderRadius: '12px',
              border: `1px solid ${step.visual.cardBorder}`,
              background: step.visual.cardBg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
                {step.title}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#64748b',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Clock size={13} />
                {formatIvrJourneyTime(step.time)}
              </div>
            </div>

            {step.detailItems.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {renderIvrDetailItems(step.detailItems)}
              </div>
            )}

          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Webhook deliveries for one call — shared with the CDR report call dialog. */
export const WebhooksPanel = ({ events }: any) => (
  <div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FileText size={16} color="#64748b" />
        <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
          Webhook Deliveries
        </span>
      </div>
      <span
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#0284c7',
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '999px',
          padding: '4px 10px',
        }}
      >
        {events.length} Events
      </span>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {events.map((hook: any, index: number) => {
        const success = isWebhookSuccess(hook);
        return (
          <div
            key={`${hook?.webhookRequestId || 'hook'}-${index}`}
            style={{
              padding: '12px 14px',
              borderRadius: '12px',
              border: `1px solid ${success ? '#86efac' : '#fecaca'}`,
              background: success ? '#f0fdf4' : '#fff1f2',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: 0,
                }}
              >
                {success ? (
                  <CheckCircle2 size={16} color="#16a34a" />
                ) : (
                  <X size={16} color="#dc2626" />
                )}
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#0f172a',
                    wordBreak: 'break-word',
                  }}
                >
                  {formatWebhookType(hook)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#64748b',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Clock size={13} />
                {formatIvrJourneyTime(hook?.createdAt)}
              </div>
            </div>

            <div
              style={{
                marginTop: '8px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                fontSize: '12px',
                color: '#475569',
              }}
            >
              <span
                style={{
                  padding: '3px 9px',
                  borderRadius: '999px',
                  background: success ? '#dcfce7' : '#fee2e2',
                  color: success ? '#15803d' : '#b91c1c',
                  fontWeight: 700,
                }}
              >
                {success ? 'DELIVERED' : 'FAILED'}
              </span>
              {hook?.webhookRequestId != null &&
              hook?.webhookRequestId !== '' ? (
                <span
                  style={{
                    padding: '3px 9px',
                    borderRadius: '999px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  Request ID: {hook.webhookRequestId}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export const formatDateTime = (dateString: any) => {
  if (!dateString) return '-';

  // Report timestamps are stored already in IST, with no zone marker. Parsing
  // them as UTC and rendering in Asia/Kolkata added a second +5:30.
  const m = moment(String(dateString), ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
  if (!m.isValid()) return '-';
  return m.format('D/M/YYYY, HH:mm:ss');
};

const getReportLegType = (row: any) => {
  const value = row?.leg_type;
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }
  return String(value).trim();
};

const formatReportLegType = (legType: any) => {
  const normalized = String(legType || '').trim().toUpperCase();
  if (!normalized) return null;
  if (normalized === 'AI' || normalized === 'AI_AGENT') return 'AI Agent';
  if (normalized.includes('WEBRTC')) return 'WEBRTC';
  if (normalized === 'MOBILE' || normalized.includes('MOBILE')) return 'Mobile';
  return String(legType).trim();
};

const parseReportMetaData = (row: any) => {
  let raw = row?.meta_data;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw;
};

export const getAiReportMeta = (mainCall: any, relatedCalls: any[] = []) => {
  const rows = [mainCall, ...(Array.isArray(relatedCalls) ? relatedCalls : [])];
  for (const row of rows) {
    const meta = parseReportMetaData(row);
    if (meta?.aiNotes) {
      return {
        aiNotes: meta.aiNotes,
        aiAgentId: meta.aiAgentId || row?.ai_agent_id || null,
      };
    }
  }
  const withId = rows.find((row) => row?.ai_agent_id || parseReportMetaData(row)?.aiAgentId);
  if (!withId) return null;
  const meta = parseReportMetaData(withId);
  return {
    aiNotes: null,
    aiAgentId: meta?.aiAgentId || withId.ai_agent_id || null,
  };
};

export const getCallInfoLegType = (mainCall: any, relatedCalls: any[] = []) => {
  const mainLeg = getReportLegType(mainCall);
  if (mainLeg) return formatReportLegType(mainLeg);

  const outboundLeg = relatedCalls.find(
    (call) =>
      call?.trace_id === mainCall?.trace_id &&
      call?.call_sid !== mainCall?.call_sid &&
      getReportLegType(call)
  );

  return outboundLeg ? formatReportLegType(getReportLegType(outboundLeg)) : null;
};

export const isAgentReportLeg = (row: any) =>
  (row?.agent_id != null && !['', '0'].includes(String(row.agent_id).trim())) ||
  String(row?.agent_name || '').trim() !== '' ||
  String(row?.agent_number || '').trim() !== '';

export const uniqueReportLegs = (rows: any[] = []) => {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const row of rows) {
    if (!row) continue;
    const key = String(row.call_sid || row.id || '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
};

const MEDIA_IDS_PER_REQUEST = 50; // backend cdr-media MAX_IDS

export const REPORT_PAGE_SIZES = [10, 25, 50, 100, 500, 1000, 2000, 4000];
export const REPORT_BATCH_SIZE = 5000;

type TraceDetail = { mainCall: any; relatedCalls: any[]; media: any[] };

// Recording / voicemail URLs for a batch of exported calls: reference ids from every
// leg are fetched 50 at a time (not once per leg); inline files resolve locally.
export const attachExportMedia = async (list: TraceDetail[]) => {
  const recIds = new Set<number>();
  const vmIds = new Set<number>();
  list.forEach((d) =>
    [d.mainCall, ...d.relatedCalls].forEach((leg: any) => {
      const ref = leg?.recording_file ? parseCdrMediaReference(leg.recording_file) : null;
      ref?.recordingIds.forEach((id: number) => recIds.add(id));
      ref?.voicemailIds.forEach((id: number) => vmIds.add(id));
    }),
  );
  const byKey = new Map<string, any>();
  const recs = [...recIds];
  const vms = [...vmIds];
  let failedLookups = 0;
  for (let k = 0; k < Math.max(recs.length, vms.length); k += MEDIA_IDS_PER_REQUEST) {
    const { recordings, voicemails, errors }: any = await fetchCdrMediaDetails({
      mode: 'reference',
      recordings: recs.slice(k, k + MEDIA_IDS_PER_REQUEST),
      voicemails: vms.slice(k, k + MEDIA_IDS_PER_REQUEST),
    });
    failedLookups += errors?.length || 0;
    [...recordings, ...voicemails].forEach((m: any) =>
      byKey.set(`${m.kind}:${m.id}`, {
        key: `${m.kind}:${m.id}`,
        type: m.kind === 'voicemail' ? 'Voicemail' : 'Recording',
        url: m.audio_url || '',
      }),
    );
  }
  list.forEach((d) => {
    const seen = new Set<string>();
    d.media = [];
    [d.mainCall, ...d.relatedCalls].forEach((leg: any) => {
      if (!leg?.recording_file) return;
      const ref = hasCdrMediaReference(leg.recording_file) ? parseCdrMediaReference(leg.recording_file) : null;
      const items = ref
        ? [
            ...ref.recordingIds.map((id: number) => byKey.get(`recording:${id}`)),
            ...ref.voicemailIds.map((id: number) => byKey.get(`voicemail:${id}`)),
          ].filter(Boolean)
        : getRecordingData(leg.recording_file).map((m: any) => ({
            key: `url:${m.url}`,
            type: String(m.type || '').toLowerCase() === 'voicemail' ? 'Voicemail' : 'Recording',
            url: m.url || '',
          }));
      items.forEach((m: any) => {
        if (seen.has(m.key)) return;
        seen.add(m.key);
        d.media.push(m);
      });
    });
  });
  return failedLookups;
};

const routeRange = (start: any, end: any) => {
  const fmt = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD'];
  const s = moment(String(start || ''), fmt, true);
  const e = moment(String(end || ''), fmt, true);
  return {
    start: (s.isValid() ? s : moment()).toDate(),
    end: (e.isValid() ? e : moment()).toDate(),
    startTime: s.isValid() && String(start).length > 10 ? s.format('HH:mm:ss') : '00:00:00',
    endTime: e.isValid() && String(end).length > 10 ? e.format('HH:mm:ss') : '23:59:59',
  };
};

const DidDetailPage = () => {
  const navigate = useNavigate();
  const { did = '', startDate, endDate } = useParams();
  const [searchParams] = useSearchParams();
  const isOutgoing = searchParams.get('type') === 'outgoing';
  const initialRange = useMemo(() => routeRange(startDate, endDate), [startDate, endDate]);
  const listUrl = isOutgoing ? API_OUTGOING_REPORT_BY_DID_LIST : API_INCOMING_REPORT_BY_DID_LIST;

  return (
    <FillPage>
      <PageHeader
        title={`DID Detail View (${isOutgoing ? 'Outgoing' : 'Incoming'})`}
        description={`One row per call for this DID, counted like the ${isOutgoing ? 'Outgoing' : 'Incoming'} Summary.`}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(isOutgoing ? `/${ROUTES.IVR.OUTCOMING_SUMMARY}` : `/${ROUTES.IVR.INCOMING_SUMMARY}`)}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      />
      <CallReportView
        key={`${did}:${isOutgoing}:${startDate}:${endDate}`}
        lockedDid={did}
        lockedDirection={isOutgoing ? 'outgoing' : 'incoming'}
        initialRange={initialRange}
        buildUrl={(query, range) => `${listUrl}/${did}/${range.start}/${range.end}?${query}`}
        traceUrlBase={listUrl}
      />
    </FillPage>
  );
};

export default DidDetailPage;
